import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ChevronLeft, ClipboardList, Send, TrendingUp, TrendingDown, Minus, X } from "lucide-react";
import { API } from "../App";
import { PerfectionismResult, PerfectionismTrendChart } from "../components/PerfectionismResult";
import { formatDateSafe } from "../lib/formatDate";

const PerfectionismResultsPage = ({ user, logout }) => {
  const { patientId } = useParams();
  const [data, setData] = useState(null);
  const [items, setItems] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/perfectionism/patient/${patientId}`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => toast.error("Résultats indisponibles"));
  useEffect(() => { load(); axios.get(`${API}/perfectionism/items`, { withCredentials: true }).then((r) => setItems(r.data)).catch(() => {}); }, [patientId]);

  const request = async () => {
    setBusy(true);
    try { await axios.post(`${API}/perfectionism/request/${patientId}`, { label }, { withCredentials: true }); toast.success("Demande envoyée : le patient verra l'invitation sur son tableau de bord"); setLabel(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const cancel = async (id) => { await axios.delete(`${API}/perfectionism/request/${id}`, { withCredentials: true }); load(); };

  const entries = data?.entries || [];
  const pending = (data?.requests || []).filter((r) => r.status === "pending");
  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-4xl mx-auto space-y-4" data-testid="perfectionism-results-page">
        <Link to={`/therapist/patients/${patientId}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="w-4 h-4" /> Fiche patient</Link>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2"><ClipboardList className="w-7 h-7 text-primary" /> Perfectionnisme & excellencisme — {data?.patient_name}</h1>

        <Card>
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-2">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Repère facultatif (ex. : début de suivi, séance 8, fin de thérapie)" className="h-9 text-sm" data-testid="perf-request-label" />
            <Button size="sm" onClick={request} disabled={busy || pending.length > 0} className="gap-1.5" data-testid="perf-request-btn"><Send className="w-4 h-4" /> Demander une passation</Button>
          </CardContent>
          {pending.length > 0 && (
            <CardContent className="pt-0 px-4 pb-3">
              {pending.map((r) => <div key={r.request_id} className="flex items-center justify-between text-xs rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 px-2 py-1.5" data-testid={`perf-pending-${r.request_id}`}><span>Passation en attente depuis le {formatDateSafe(r.created_at, { day: "numeric", month: "short" })}{r.label ? ` · ${r.label}` : ""}</span><button onClick={() => cancel(r.request_id)} className="text-muted-foreground hover:text-foreground" title="Annuler"><X className="w-3.5 h-3.5" /></button></div>)}
            </CardContent>
          )}
        </Card>

        {entries.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="perf-no-entries">Aucune passation pour l'instant. Le patient peut aussi remplir le questionnaire librement depuis son espace.</p> : (
          <>
            {data.trend && (
              <Card data-testid="perf-trend-summary"><CardHeader className="pb-2"><CardTitle className="text-base">Première → dernière passation ({entries.length})</CardTitle></CardHeader>
                <CardContent className="grid sm:grid-cols-3 gap-2">
                  {Object.entries(data.trend).map(([k, v]) => (
                    <div key={k} className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">{v.label}</p>
                      <p className="text-lg font-semibold flex items-center gap-2">{v.first} → {v.last} {v.delta === 0 ? <Minus className="w-4 h-4 text-muted-foreground" /> : v.improved ? <TrendingUp className="w-4 h-4 text-emerald-600" /> : <TrendingDown className="w-4 h-4 text-rose-600" />}</p>
                      <p className={`text-xs ${v.delta === 0 ? "text-muted-foreground" : v.improved ? "text-emerald-600" : "text-rose-600"}`}>{v.delta > 0 ? "+" : ""}{v.delta} {v.delta === 0 ? "" : v.improved ? "(favorable)" : "(défavorable)"}</p>
                    </div>
                  ))}
                </CardContent></Card>
            )}
            {entries.length >= 2 && <PerfectionismTrendChart entries={entries} subscales={items?.subscales} />}
            <PerfectionismResult scoring={entries[entries.length - 1].scoring} subscales={items?.subscales} title={`Dernière passation — ${formatDateSafe(entries[entries.length - 1].created_at, { day: "numeric", month: "long", year: "numeric" })}${entries[entries.length - 1].label ? ` · ${entries[entries.length - 1].label}` : ""}`} />
            {entries[entries.length - 1].note && <p className="text-sm text-muted-foreground italic">Note du patient : « {entries[entries.length - 1].note} »</p>}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PerfectionismResultsPage;
